import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import moment from 'moment';
import { getOdcOrderById, getOdcCustomers, getOdcPaymentModes, settleOdcOrder, dispatchOdcOrder } from '../utilities/api';
import ThermalReceipt from './ThermalReceipt';
import TaxInvoice from './TaxInvoice';

const OdcView = ({ billIdFromProps, isDrawer }) => {
    const { id: urlId } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    const activeId = billIdFromProps || urlId;

    const [loading, setLoading] = useState(true);
    const [dispatching, setDispatching] = useState(false);
    const [settling, setSettling] = useState(false);
    const [showPageSizeModal, setShowPageSizeModal] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [paymentModes, setPaymentModes] = useState([]);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const [settlementDate, setSettlementDate] = useState(new Date());
    const [remarks, setRemarks] = useState('');
    const [settleOpen, setSettleOpen] = useState(false);
    const [data, setData] = useState({
        header: null,
        items: [],
        taxSummary: [],
        outlet: null,
        customer: null,
        status: '',
        settlementInfo: null
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const formatCurr = (num) => parseFloat(num || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handlePrint = (pageSize = 'A5') => {
        const el = document.getElementById('printable-area');
        if (!el) return;
        setShowPageSizeModal(false);

        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map((n) => n.outerHTML)
            .join('\n');

        const isA4 = pageSize === 'A4';

        const iframe = document.createElement('iframe');
        Object.assign(iframe.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '0',
            height: '0',
            border: 'none',
            opacity: '0'
        });
        document.body.appendChild(iframe);

        iframe.contentDocument.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8"/>
                ${styles}
                <style>
                    @page {
                        size: ${isA4 ? 'A4' : 'A5'} portrait;
                        margin: ${isA4 ? '12mm 18mm' : '8mm 10mm'};
                    }

                    * {
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }

                    html, body {
                        width: 100%;
                        margin: 0;
                        padding: 0;
                        background: #fff;
                        color: #000;
                    }

                    /* Flatten screen wrappers */
                    .ti-wrap {
                        display: block !important;
                        padding: 0 !important;
                        background: none !important;
                        min-height: unset !important;
                    }

                    .ti-invoice {
                        width: 100% !important;
                        min-height: unset !important;
                        height: auto !important;
                        box-shadow: none !important;
                        padding: 0 !important;
                    }

                    /* Strip the card chrome */
                    .odcv-receipt-card {
                        border: none !important;
                        box-shadow: none !important;
                        border-radius: 0 !important;
                    }
                    .odcv-receipt-body {
                        padding: 0 !important;
                        display: block !important;
                    }

                    /* Remove top spacer <br> tags */
                    .ti-invoice br { display: none !important; }

                    body > * {
                        width: 100% !important;
                        max-width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        box-shadow: none !important;
                        border: none !important;
                        background: #fff !important;
                    }

                    /* Title */
                    .ti-title h2 {
                        font-size: ${isA4 ? '20px' : '16px'} !important;
                        letter-spacing: ${isA4 ? '4px' : '2px'} !important;
                        margin-bottom: ${isA4 ? '6px' : '3px'} !important;
                    }

                    /* Meta */
                    .ti-meta-left p, .ti-meta-right p, .ti-bill-no {
                        font-size: ${isA4 ? '13px' : '11px'} !important;
                    }

                    /* Table */
                    table {
                        width: 100% !important;
                        border-collapse: collapse;
                    }
                    .ti-table thead th {
                        font-size: ${isA4 ? '11px' : '9px'} !important;
                        padding: ${isA4 ? '3px 4px' : '2px 3px'} !important;
                    }
                    .ti-table tbody td {
                        font-size: ${isA4 ? '11px' : '9.5px'} !important;
                        padding: ${isA4 ? '2px 4px' : '1px 3px'} !important;
                        line-height: ${isA4 ? '1.35' : '1.15'} !important;
                    }

                    /* Totals */
                    .ti-amt-line {
                        font-size: ${isA4 ? '13px' : '11px'} !important;
                        padding: ${isA4 ? '2px 0' : '1px 0'} !important;
                    }
                    .ti-amounts {
                        min-width: ${isA4 ? '260px' : '210px'} !important;
                    }

                    /* Footer */
                    .ti-footer-codes {
                        font-size: ${isA4 ? '13px' : '11px'} !important;
                    }
                    .ti-sig .ti-thank {
                        font-size: ${isA4 ? '14px' : '12px'} !important;
                        margin-bottom: ${isA4 ? '28px' : '20px'} !important;
                    }
                    .ti-sig .ti-manager {
                        font-size: ${isA4 ? '11px' : '9px'} !important;
                    }

                    /* Settlement box */
                    .ti-settlement-box {
                        padding: ${isA4 ? '8px 12px' : '6px 8px'} !important;
                        margin-top: ${isA4 ? '8px' : '4px'} !important;
                        font-size: ${isA4 ? '12px' : '10px'} !important;
                    }

                    /* Dotted dividers */
                    .ti-dotted { margin: ${isA4 ? '4px 0' : '2px 0'} !important; }

                    /* Totals row gap */
                    .ti-totals-row { margin-top: ${isA4 ? '6px' : '3px'} !important; }

                    /* No page break inside rows */
                    tr, .no-break { page-break-inside: avoid; break-inside: avoid; }

                    img {
                        max-width: 100% !important;
                        height: auto !important;
                    }

                    .no-print { display: none !important; }
                </style>
            </head>
            <body>${el.innerHTML}</body>
            </html>
        `);
        iframe.contentDocument.close();

        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            document.body.removeChild(iframe);
        };
    };

    const fetchData = () => {
        if (!activeId) return;
        setLoading(true);
        getOdcOrderById(activeId)
            .then((res) => {
                const parsed = res.final_output ? JSON.parse(res.final_output) : res;
                setData({
                    header: parsed.header,
                    items: parsed.items || [],
                    taxSummary: parsed.tax_summary || [],
                    outlet: parsed.outlet,
                    customer: parsed.customer,
                    status: res.status || parsed.header?.status || '',
                    settlementInfo: res.settlement_info || parsed.settlement_info || null
                });
                if (parsed.customer?.id) setSelectedCustomer(parsed.customer.id);
                const bId = parsed.outlet?.branch_id || parsed.header?.branch_id;
                if (bId) {
                    getOdcCustomers(bId).then((r) => setCustomers(r || []));
                    getOdcPaymentModes(bId).then((r) => setPaymentModes(r || []));
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
    }, [activeId]);
    const handleSettle = () => {
        setSettling(true);
        settleOdcOrder({
            order_id: activeId,
            payment_mode: selectedPaymentMode,
            settlement_date: moment(settlementDate).format('YYYY-MM-DD'),
            remarks: remarks
        })
            .then((res) => {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.message });

                // Refresh component state so the sidebar appears
                fetchData();
                setSettleOpen(false);
            })
            .catch((err) => {
                // Failure Toast
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: err.response?.data?.message || 'Transaction Failed'
                });
            })
            .finally(() => setSettling(false));
    };
    const handleDispatch = () => {
        confirmDialog({
            message: 'Confirm Order Dispatch?',
            header: 'Dispatch',
            icon: 'pi pi-send',
            accept: () => {
                setDispatching(true);
                dispatchOdcOrder({
                    order_id: activeId,
                    customer_id: selectedCustomer,
                    branchname: localStorage.getItem('branch'),
                    branch_id: localStorage.getItem('branchid')
                })
                    .then(() => {
                        toast.current.show({ severity: 'success', summary: 'Order Dispatched Successfully!' });
                        isDrawer ? fetchData() : setTimeout(() => navigate('/odcorderlist'), 1500);
                    })
                    .finally(() => setDispatching(false));
            }
        });
    };

    const currentStatus = data.status?.toLowerCase();
    const isPending = currentStatus === 'pending';
    const isDispatched = currentStatus === 'dispatched';
    const isSettled = currentStatus === 'settled';
    const isCancelled = currentStatus === 'cancelled';
    const rolename = localStorage.getItem('role');
    const selectedPaymentModeLabel = paymentModes.find((pm) => pm.id === (data.settlementInfo?.payment_mode ?? selectedPaymentMode))?.name;

    const statusConfig = {
        pending: { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', icon: 'pi-clock', label: 'PENDING' },
        dispatched: { color: '#0369a1', bg: '#eff6ff', border: '#93c5fd', icon: 'pi-send', label: 'DISPATCHED' },
        settled: { color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', icon: 'pi-check-circle', label: 'SETTLED' },
        cancelled: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: 'pi-times-circle', label: 'CANCELLED' }
    };
    const sc = statusConfig[currentStatus] || statusConfig.pending;

    if (loading)
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    background: '#f8f7f4',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
            `}</style>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: '#c9a84c',
                                animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`
                            }}
                        />
                    ))}
                </div>
                <span style={{ fontFamily: "'DM Sans', sans-serif", color: '#6b6b6b', fontSize: '0.875rem', letterSpacing: '0.08em' }}>LOADING ORDER</span>
            </div>
        );

    return (
        <div className="odcv-root">
            <Toast ref={toast} />
            {!isDrawer && <ConfirmDialog />}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

                * { box-sizing: border-box; }

                .odcv-root {
                    font-family: 'DM Sans', sans-serif;
                    background: #f8f7f4;
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                }

                /* ═══ TOP HEADER BAR ═══ */
                .odcv-header {
                    background: #1c1c1e;
                    color: #fff;
                    padding: 0 2rem;
                    height: 64px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    box-shadow: 0 2px 20px rgba(0,0,0,0.25);
                    gap: 1rem;
                }

                .odcv-header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    flex: 1;
                    min-width: 0;
                }

                .odcv-back-btn {
                    background: none;
                    border: 1px solid rgba(255,255,255,0.15);
                    color: #fff;
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    flex-shrink: 0;
                }
                .odcv-back-btn:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.3); }

                .odcv-title-group { min-width: 0; }
                .odcv-order-num {
                    font-family: 'Cormorant Garamond', serif;
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #fff;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 1.2;
                }
                .odcv-outlet-name {
                    font-size: 0.72rem;
                    color: rgba(255,255,255,0.5);
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    margin-top: 1px;
                }

                .odcv-status-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    letter-spacing: 0.1em;
                    border: 1px solid;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .odcv-header-right {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-shrink: 0;
                }

                .odcv-print-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #c9a84c;
                    color: #1c1c1e;
                    border: none;
                    padding: 8px 18px;
                    border-radius: 8px;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 0.8rem;
                    font-weight: 600;
                    letter-spacing: 0.06em;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }
                .odcv-print-btn:hover { background: #b8942e; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(201,168,76,0.4); }

                /* ═══ CONTENT AREA ═══ */
                .odcv-body {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 2rem 1.5rem;
                    gap: 1.5rem;
                }

                /* ═══ DISPATCH PANEL ═══ */
                .odcv-dispatch-panel {
                    width: 100%;
                    max-width: 720px;
                    background: #fff;
                    border-radius: 14px;
                    border: 1px solid #e9e5df;
                    overflow: hidden;
                    box-shadow: 0 2px 16px rgba(0,0,0,0.05);
                    animation: slideDown 0.35s ease;
                }
                @keyframes slideDown { from { opacity:0; transform: translateY(-12px); } to { opacity:1; transform: translateY(0); } }

                .odcv-panel-header {
                    padding: 1rem 1.5rem;
                    background: #faf9f6;
                    border-bottom: 1px solid #e9e5df;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .odcv-panel-header-icon {
                    width: 32px; height: 32px;
                    border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.9rem;
                }
                .odcv-panel-title {
                    font-size: 0.8rem;
                    font-weight: 600;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: #3d3d3d;
                }

                .odcv-panel-body { padding: 1.25rem 1.5rem; }

                .odcv-field-row {
                    display: flex;
                    gap: 1rem;
                    align-items: flex-end;
                    flex-wrap: wrap;
                }
                .odcv-field { flex: 1; min-width: 200px; }
                .odcv-field label {
                    display: block;
                    font-size: 0.72rem;
                    font-weight: 500;
                    color: #888;
                    letter-spacing: 0.07em;
                    text-transform: uppercase;
                    margin-bottom: 6px;
                }
                .odcv-field .p-dropdown,
                .odcv-field .p-calendar { width: 100% !important; }

                .odcv-dispatch-btn {
                    background: #1c1c1e !important;
                    border-color: #1c1c1e !important;
                    color: #fff !important;
                    border-radius: 8px !important;
                    padding: 0.65rem 1.5rem !important;
                    font-weight: 600 !important;
                    letter-spacing: 0.05em !important;
                    transition: all 0.2s !important;
                    white-space: nowrap;
                }
                .odcv-dispatch-btn:hover:not(:disabled) { background: #333 !important; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(0,0,0,0.2) !important; }

                /* ═══ RECEIPT CARD ═══ */
                .odcv-receipt-card {
                    width: 100%;
                    max-width: 720px;
                    background: #fff;
                    border-radius: 14px;
                    border: 1px solid #e9e5df;
                    overflow: hidden;
                    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
                }
                .odcv-receipt-label {
                    padding: 0.65rem 1.5rem;
                    background: #faf9f6;
                    border-bottom: 1px solid #e9e5df;
                    font-size: 0.7rem;
                    font-weight: 600;
                    letter-spacing: 0.12em;
                    text-transform: uppercase;
                    color: #999;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .odcv-receipt-body {
                    padding: 2rem;
                    display: flex;
                    justify-content: center;
                }

                /* ═══ SETTLEMENT PANEL ═══ */
                .odcv-settle-panel {
                    width: 100%;
                    max-width: 720px;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 2px 16px rgba(0,0,0,0.05);
                    animation: slideUp 0.35s ease;
                }
                @keyframes slideUp { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform: translateY(0); } }

                .odcv-settle-toggle {
                    width: 100%;
                    background: linear-gradient(135deg, #1c1c1e 0%, #2d2d30 100%);
                    color: #fff;
                    border: none;
                    padding: 1.1rem 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    font-family: 'DM Sans', sans-serif;
                    transition: all 0.2s;
                }
                .odcv-settle-toggle:hover { background: linear-gradient(135deg, #2a2a2c 0%, #383838 100%); }
                .odcv-settle-toggle-left { display: flex; align-items: center; gap: 12px; }
                .odcv-settle-gold-dot {
                    width: 10px; height: 10px;
                    border-radius: 50%;
                    background: #c9a84c;
                    box-shadow: 0 0 8px rgba(201,168,76,0.6);
                }
                .odcv-settle-toggle-title {
                    font-size: 0.85rem;
                    font-weight: 600;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                }
                .odcv-settle-toggle-sub {
                    font-size: 0.72rem;
                    color: rgba(255,255,255,0.45);
                    margin-top: 1px;
                }
                .odcv-settle-chevron {
                    font-size: 0.9rem;
                    color: rgba(255,255,255,0.6);
                    transition: transform 0.3s;
                }
                .odcv-settle-chevron.open { transform: rotate(180deg); }

                .odcv-settle-form {
                    background: #fff;
                    border: 1px solid #e9e5df;
                    border-top: none;
                    border-radius: 0 0 14px 14px;
                    padding: 1.5rem;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }
                @media (max-width: 600px) { .odcv-settle-form { grid-template-columns: 1fr; } }

                .odcv-settle-field label {
                    display: block;
                    font-size: 0.72rem;
                    font-weight: 500;
                    color: #888;
                    letter-spacing: 0.07em;
                    text-transform: uppercase;
                    margin-bottom: 6px;
                }
                .odcv-settle-field .p-dropdown,
                .odcv-settle-field .p-calendar { width: 100% !important; }
                .odcv-settle-field.full { grid-column: 1 / -1; }

                .odcv-settle-field .p-inputtextarea { 
                    width: 100% !important; 
                    resize: none !important;
                    border-radius: 8px !important;
                }

                .odcv-confirm-btn {
                    background: linear-gradient(135deg, #c9a84c 0%, #b8942e 100%) !important;
                    border: none !important;
                    color: #1c1c1e !important;
                    font-weight: 700 !important;
                    letter-spacing: 0.06em !important;
                    border-radius: 8px !important;
                    width: 100% !important;
                    padding: 0.75rem !important;
                    font-size: 0.85rem !important;
                    transition: all 0.2s !important;
                }
                .odcv-confirm-btn:hover:not(:disabled) { 
                    box-shadow: 0 4px 16px rgba(201,168,76,0.5) !important;
                    transform: translateY(-1px) !important;
                }

                /* ═══ SETTLED INFO ═══ */
                .odcv-settled-info {
                    width: 100%;
                    max-width: 720px;
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    border: 1px solid #6ee7b7;
                    border-radius: 14px;
                    padding: 1.25rem 1.5rem;
                    display: flex;
                    gap: 2rem;
                    flex-wrap: wrap;
                    animation: slideUp 0.35s ease;
                }
                .odcv-settled-info-item {}
                .odcv-settled-info-item .si-label {
                    font-size: 0.68rem;
                    font-weight: 600;
                    letter-spacing: 0.1em;
                    text-transform: uppercase;
                    color: #059669;
                    margin-bottom: 4px;
                }
                .odcv-settled-info-item .si-value {
                    font-size: 0.95rem;
                    font-weight: 500;
                    color: #064e3b;
                }

                /* ═══ PAGE SIZE MODAL ═══ */
                .psize-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.55);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    backdrop-filter: blur(3px);
                }
                .psize-modal {
                    background: #fff;
                    border-radius: 18px;
                    padding: 2rem 2.5rem 1.75rem;
                    text-align: center;
                    box-shadow: 0 24px 64px rgba(0,0,0,0.35);
                    min-width: 340px;
                    animation: psSlide 0.22s ease;
                }
                @keyframes psSlide {
                    from { opacity: 0; transform: scale(0.88) translateY(12px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
                .psize-modal-title {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #1c1c1e;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }
                .psize-modal-sub {
                    font-size: 0.78rem;
                    color: #aaa;
                    margin-bottom: 1.75rem;
                    letter-spacing: 0.03em;
                }
                .psize-options {
                    display: flex;
                    gap: 1rem;
                    justify-content: center;
                    margin-bottom: 1.25rem;
                }
                .psize-btn {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    padding: 1.25rem 1.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 14px;
                    background: #faf9f6;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 120px;
                    font-family: 'DM Sans', sans-serif;
                    outline: none;
                }
                .psize-btn:hover {
                    border-color: #c9a84c;
                    background: #fffbf0;
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(201,168,76,0.22);
                }
                .psize-page-rect {
                    border: 2px solid #1c1c1e;
                    background: #fff;
                    border-radius: 2px;
                    box-shadow: 2px 2px 0 rgba(0,0,0,0.12);
                }
                .psize-btn-label {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #1c1c1e;
                    letter-spacing: 0.1em;
                }
                .psize-btn-dim {
                    font-size: 0.68rem;
                    color: #bbb;
                    letter-spacing: 0.04em;
                }
                .psize-cancel {
                    background: none;
                    border: none;
                    color: #bbb;
                    font-size: 0.82rem;
                    cursor: pointer;
                    padding: 6px 16px;
                    border-radius: 8px;
                    font-family: 'DM Sans', sans-serif;
                    transition: color 0.18s;
                    letter-spacing: 0.04em;
                }
                .psize-cancel:hover { color: #555; }

                /* No print styles needed — handled by custom print window */

                /* PrimeReact tweaks */
                .odcv-root .p-dropdown { border-radius: 8px !important; font-size: 0.875rem !important; }
                .odcv-root .p-calendar .p-inputtext { border-radius: 8px !important; font-size: 0.875rem !important; }
                .odcv-root .p-inputtextarea { font-size: 0.875rem !important; }
            `}</style>

            {/* ═══════════════════════════════
                TOP HEADER BAR
            ═══════════════════════════════ */}
            <header className="odcv-header no-print">
                <div className="odcv-header-left">
                    {!isDrawer && (
                        <button className="odcv-back-btn" onClick={() => navigate(-1)} title="Go back">
                            <i className="pi pi-arrow-left" style={{ fontSize: '0.85rem' }} />
                        </button>
                    )}
                    <div className="odcv-title-group">
                        {/* <div className="odcv-order-num">{data.header?.bill_no || data.header?.order_no || `Order #${activeId}`}</div> */}
                        <div className="odcv-outlet-name">{data.outlet?.name || data.header?.outlet_name || 'Outdoor Catering'}</div>
                    </div>

                    <div
                        className="odcv-status-badge"
                        style={{
                            color: sc.color,
                            background: sc.bg,
                            borderColor: sc.border
                        }}
                    >
                        <i className={`pi ${sc.icon}`} style={{ fontSize: '0.7rem' }} />
                        {sc.label}
                    </div>
                </div>

                <div className="odcv-header-right">
                    {data.header?.event_date && (
                        <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="pi pi-calendar" style={{ fontSize: '0.72rem' }} />
                            {formatDate(data.header.event_date)}
                        </span>
                    )}
                    {data.header?.net_amount && (
                        <span
                            style={{
                                fontSize: '1rem',
                                fontFamily: "'Cormorant Garamond', serif",
                                fontWeight: 600,
                                color: '#c9a84c',
                                letterSpacing: '0.02em'
                            }}
                        >
                            ₹{formatCurr(data.header.net_amount)}
                        </span>
                    )}
                    <button className="odcv-print-btn" onClick={() => setShowPageSizeModal(true)}>
                        <i className="pi pi-print" style={{ fontSize: '0.8rem' }} />
                        {isPending || isCancelled ? 'PRINT RECEIPT' : 'PRINT INVOICE'}
                    </button>
                </div>
            </header>

            {/* ═══════════════════════════════
                BODY
            ═══════════════════════════════ */}
            <div className="odcv-body">
                {/* DISPATCH PANEL (only when pending) */}
                {isPending && !isCancelled && (
                    <div className="odcv-dispatch-panel no-print">
                        <div className="odcv-panel-header">
                            <div className="odcv-panel-header-icon" style={{ background: '#eff6ff' }}>
                                <i className="pi pi-send" style={{ color: '#0369a1', fontSize: '0.85rem' }} />
                            </div>
                            <div>
                                <div className="odcv-panel-title">Assign & Dispatch Order</div>
                            </div>
                        </div>
                        <div className="odcv-panel-body">
                            <div className="odcv-field-row">
                                <div className="odcv-field">
                                    <label>Assign to Customer</label>
                                    <Dropdown value={selectedCustomer} options={customers} onChange={(e) => setSelectedCustomer(e.value)} optionLabel="name" optionValue="id" placeholder="Select Customer" filter />
                                </div>
                                <Button label="DISPATCH" icon="pi pi-send" className="odcv-dispatch-btn" onClick={handleDispatch} loading={dispatching} disabled={!selectedCustomer} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ RECEIPT / INVOICE PREVIEW ═══ */}
                <div className="odcv-receipt-card" id="printable-area">
                    <div className="odcv-receipt-label no-print">
                        <i className="pi pi-file" style={{ fontSize: '0.75rem' }} />
                        {isPending || isCancelled ? 'THERMAL RECEIPT PREVIEW' : 'TAX INVOICE PREVIEW'}
                    </div>
                    <div className="odcv-receipt-body">
                        {(isPending || isCancelled) && <ThermalReceipt header={data.header} items={data.items} isCancelled={isCancelled} formatDate={formatDate} />}
                        {(isDispatched || isSettled) && (
                            <TaxInvoice
                                header={data.header}
                                items={data.items}
                                customer={data.customer}
                                formatDate={formatDate}
                                formatCurr={formatCurr}
                                settlementInfo={isSettled && data.settlementInfo ? { ...data.settlementInfo, payment_mode_label: selectedPaymentModeLabel } : null}
                            />
                        )}
                    </div>
                </div>

                {/* ═══ SETTLED INFO STRIP ═══ */}
                {isSettled && data.settlementInfo && (
                    <div className="odcv-settled-info no-print">
                        <div className="odcv-settled-info-item">
                            <div className="si-label">Payment Mode</div>
                            <div className="si-value">{selectedPaymentModeLabel || '—'}</div>
                        </div>
                        {data.settlementInfo?.settlement_date && (
                            <div className="odcv-settled-info-item">
                                <div className="si-label">Settled On</div>
                                <div className="si-value">{formatDate(data.settlementInfo.settlement_date)}</div>
                            </div>
                        )}
                        {data.settlementInfo?.remarks && (
                            <div className="odcv-settled-info-item">
                                <div className="si-label">Remarks</div>
                                <div className="si-value">{data.settlementInfo.remarks}</div>
                            </div>
                        )}
                        <div className="odcv-settled-info-item" style={{ marginLeft: 'auto' }}>
                            <div className="si-label">Amount</div>
                            <div className="si-value" style={{ fontSize: '1.15rem', fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>
                                ₹{formatCurr(data.header?.net_amount)}
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ PAYMENT SETTLEMENT PANEL (CEO + Dispatched) ═══ */}
                {isDispatched && rolename === 'CEO' && (
                    <div className="odcv-settle-panel no-print">
                        <button className="odcv-settle-toggle" onClick={() => setSettleOpen((o) => !o)}>
                            <div className="odcv-settle-toggle-left">
                                <div className="odcv-settle-gold-dot" />
                                <div>
                                    <div className="odcv-settle-toggle-title">Payment Settlement</div>
                                    <div className="odcv-settle-toggle-sub">Record payment and close this order</div>
                                </div>
                            </div>
                            <i className={`pi pi-chevron-down odcv-settle-chevron ${settleOpen ? 'open' : ''}`} />
                        </button>

                        {settleOpen && (
                            <div className="odcv-settle-form">
                                <div className="odcv-settle-field">
                                    <label>Payment Mode</label>
                                    <Dropdown value={selectedPaymentMode} options={paymentModes} onChange={(e) => setSelectedPaymentMode(e.value)} optionLabel="name" optionValue="id" placeholder="Select mode" />
                                </div>
                                <div className="odcv-settle-field">
                                    <label>Settlement Date</label>
                                    <Calendar value={settlementDate} onChange={(e) => setSettlementDate(e.value)} dateFormat="dd-mm-yy" showIcon />
                                </div>
                                <div className="odcv-settle-field full">
                                    <label>Remarks</label>
                                    <InputTextarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} placeholder="Add settlement notes…" />
                                </div>
                                <div className="odcv-settle-field full">
                                    <Button
                                        label="CONFIRM SETTLEMENT"
                                        icon="pi pi-check-circle"
                                        className="odcv-confirm-btn"
                                        loading={settling}
                                        onClick={handleSettle} // Updated this line
                                        disabled={!selectedPaymentMode}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Spacer */}
                <div style={{ height: '2rem' }} />
            </div>

            {/* ═══ PAGE SIZE SELECTOR MODAL ═══ */}
            {showPageSizeModal && (
                <div className="psize-overlay" onClick={() => setShowPageSizeModal(false)}>
                    <div className="psize-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="psize-modal-title">Select Page Size</div>
                        <div className="psize-modal-sub">Choose paper size for the invoice</div>
                        <div className="psize-options">
                            {/* A4 — larger icon */}
                            <button className="psize-btn" onClick={() => handlePrint('A4')}>
                                <div className="psize-page-rect" style={{ width: 44, height: 62, padding: '6px 5px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {[1,2,3,4,5].map(i => <div key={i} style={{ height: 2, background: '#ccc', borderRadius: 1 }} />)}
                                </div>
                                <span className="psize-btn-label">A4</span>
                                <span className="psize-btn-dim">210 × 297 mm</span>
                            </button>
                            {/* A5 — smaller icon */}
                            <button className="psize-btn" onClick={() => handlePrint('A5')}>
                                <div className="psize-page-rect" style={{ width: 32, height: 45, padding: '5px 4px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {[1,2,3].map(i => <div key={i} style={{ height: 2, background: '#ccc', borderRadius: 1 }} />)}
                                </div>
                                <span className="psize-btn-label">A5</span>
                                <span className="psize-btn-dim">148 × 210 mm</span>
                            </button>
                        </div>
                        <button className="psize-cancel" onClick={() => setShowPageSizeModal(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OdcView;
